import { fieldsToHide } from 'src/data-capture/merge-review/ReviewHelperFunctions';
import { FormFieldNew } from 'src/non-financial-data/components/FormFieldNew';
import { useDynamicUI } from '../DynamicUiContext';
import { Logger } from 'src/utils/Logger';
import { UiField } from 'src/UI/dynamicUI/types';
import { FieldTypeMetadata } from 'src/data-capture/types';

function getNestedProperty<T = unknown>(
  obj: Record<PropertyKey, unknown>,
  path: string
): T | undefined {
  if (!path) return obj as T;
  return path.split('.').reduce<unknown>((curr, key) => {
    if (curr == null || typeof curr !== 'object') return undefined;
    return Object.hasOwn(curr as object, key)
      ? (curr as Record<string, unknown>)[key]
      : undefined;
  }, obj) as T | undefined;
}

interface DynamicFieldProps {
  field: UiField;
  index?: number;
  readModePrepend?: string;
  readModeAppend?: string;
  hideLabel?: boolean;
  isEditing?: boolean;
  onValueChange?: (value: unknown, fieldName: string) => void;
  disabled?: boolean;
}

export default function DynamicField({
  field,
  index,
  readModePrepend,
  readModeAppend,
  onValueChange,
  disabled = false,
  hideLabel = false,
}: DynamicFieldProps) {
  const { form, uiData } = useDynamicUI();

  if (fieldsToHide.includes(field.entityField)) {
    return null;
  }

  const entityWithMetadata = uiData?.entities[field.entityType];

  if (!entityWithMetadata) {
    return null;
  }

  const parentFieldName = index !== undefined ? `${field.entityType}.${index}` : field.entityType;

  const formKey = form.key(`${parentFieldName}.${field.entityField}`);

  const retrieveMetadata = (): FieldTypeMetadata => {
    try {
      const entityMetadata = uiData?.ui.entitiesMetadata[field.entityType];
      if (!entityMetadata) {
        throw new Error(`No metadata found for entity type: ${field.entityType}`);
      }

      const metadata = getNestedProperty<FieldTypeMetadata>(entityMetadata, field.entityField);
      if (!metadata) {
        throw new Error(`Field path ${field.entityField} not found in metadata`);
      }

      return metadata;
    } catch (error) {
      Logger.error('Failed to retrieve metadata', error, {
        fieldType: field.entityType,
        fieldName: field.entityField,
        uiData: JSON.stringify(uiData?.ui.entitiesMetadata),
      });
      throw error;
    }
  };

  return (
    <FormFieldNew
      key={formKey}
      form={form}
      parentFieldName={parentFieldName}
      fieldName={field.entityField}
      label={field.label}
      entityMetadata={retrieveMetadata()}
      readModePrepend={readModePrepend}
      readModeAppend={readModeAppend}
      hideLabel={hideLabel}
      onChange={onValueChange}
      disabled={disabled}
    />
  );
}
